import { type ReactNode, type FC } from "react";
import { useFormContext } from "react-hook-form";
import { Checkbox } from "@root/shared/components/Checkbox";
import { TextArea } from "@root/shared/components/TextArea";
import { InformationText } from "@root/shared/components/InformationText";
import { Heading } from "@root/shared/components/Heading";
import { Label } from "@root/shared/components/Label";
import * as styles from "./styles.css";

type Option = {
  optionCode: string;
  label: string;
  requiresFollowUpText: boolean;
};

type FormField = {
  name: string;
  followUpTextName: string;
};

type Props = {
  question: ReactNode;
  options: Option[];
  formField: FormField;
};

const PLACEHOLDER = "「その他」の場合は、具体的な内容を必ずご入力ください。";

export const MultipleChoiceQuestion: FC<Props> = ({ question, options, formField }) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const checkboxError = errors[formField.name];
  const followUpTextError = errors[formField.followUpTextName];

  const watchedValue = watch(formField.name);
  const selectedOptionCodes: string[] = Array.isArray(watchedValue) ? watchedValue : [];
  const requiresFollowUpText = options.some(
    (option) => option.requiresFollowUpText && selectedOptionCodes.includes(option.optionCode),
  );

  return (
    <>
      <Heading tag="p" size="medium" bold className={styles.heading}>
        {question}
        <Label theme="lightGray" className={styles.headingLabel}>
          複数選択可
        </Label>
        <Label theme="red" className={styles.headingLabel}>
          必須
        </Label>
      </Heading>
      {checkboxError && (
        <div className={styles.errorText}>
          <InformationText text={checkboxError.message} isAlert />
        </div>
      )}
      <ul className={styles.list}>
        {options.map((option) => {
          const id = `checkbox-${formField.name}-${option.optionCode}`;
          return (
            <li key={option.optionCode} className={styles.listItem}>
              <Checkbox
                id={id}
                value={option.optionCode}
                label={option.label}
                {...register(formField.name)}
                hasError={!!checkboxError}
              />
            </li>
          );
        })}
      </ul>
      {requiresFollowUpText && (
        <div className={styles.otherTextArea}>
          {followUpTextError && (
            <div className={styles.errorText}>
              <InformationText text={followUpTextError.message} isAlert />
            </div>
          )}
          <TextArea
            {...register(formField.followUpTextName)}
            placeholder={PLACEHOLDER}
            hasError={!!followUpTextError}
          />
        </div>
      )}
    </>
  );
};
